/**
 * A lookup picker that can add to its own list.
 *
 * Industries, sources and lost reasons are open sets — the business will always
 * meet one that is not there yet, and making that a trip to an admin screen
 * means people pick "Other" instead, which quietly destroys the grouping the
 * lookup existed for.
 *
 * Statuses deliberately do not use this: they are structural, and a pipeline
 * anyone can extend is how a status list grows back to thirty-five stages.
 */

import { useEffect, useState } from 'react'
import { useFetcher } from 'react-router-dom'
import type { ExtensibleLookup, LookupItem } from '@erp/contracts'
import { Button } from '../ui'

const ADD = '__add__'

export function LookupSelect({
  name,
  kind,
  items,
  defaultValue,
  placeholder = 'None',
}: {
  name: string
  kind: ExtensibleLookup
  items: readonly LookupItem[]
  defaultValue?: string | null
  placeholder?: string
}) {
  const fetcher = useFetcher<LookupItem>()

  // Controlled internally, uncontrolled from the caller's point of view: the
  // enclosing form reads the value from FormData on submit.
  const [value, setValue] = useState(defaultValue ?? '')
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')

  // Selecting the row the server just created — which may be a pre-existing one
  // if the name was already taken.
  const created = fetcher.data
  useEffect(() => {
    if (!created) return
    setValue(created.id)
    setAdding(false)
    setDraft('')
  }, [created])

  function submit() {
    const trimmed = draft.trim()
    if (trimmed.length === 0) return
    fetcher.submit(
      { intent: 'lookup.create', kind, name: trimmed },
      { method: 'post', action: '/crm/lookups' },
    )
  }

  return (
    <div className="ax-lookup">
      <select
        className="ax-select"
        name={name}
        value={value}
        onChange={(event) => {
          const next = event.currentTarget.value
          if (next === ADD) {
            setAdding(true)
            return
          }
          setValue(next)
        }}
      >
        <option value="">{placeholder}</option>
        {items.map((item) => (
          <option key={item.id} value={item.id}>
            {item.name}
          </option>
        ))}
        <option value={ADD}>+ Add new…</option>
      </select>

      {adding ? (
        <div className="ax-lookup-add">
          {/* Not a nested <form> — that is invalid HTML and would submit the
              enclosing entity form. A plain input plus a button instead. */}
          <input
            className="ax-input"
            value={draft}
            autoFocus
            placeholder="New value"
            onChange={(event) => setDraft(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                submit()
              }
              if (event.key === 'Escape') setAdding(false)
            }}
          />
          <Button onClick={submit} disabled={fetcher.state !== 'idle' || draft.trim() === ''}>
            {fetcher.state === 'idle' ? 'Add' : 'Adding…'}
          </Button>
          <button type="button" className="ax-link" onClick={() => setAdding(false)}>
            Cancel
          </button>
        </div>
      ) : null}
    </div>
  )
}
